'use client';

import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Button } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';

import { AchievementCardSkeleton } from '@/modules/players/v3/components/player-pages/achievements/list/achievement-card-skeleton';
import { MascotEmptyState } from '@/modules/players/v3/components/player-pages/achievements/list/public/mascot-empty-state';
import { AchievementCard } from '@/modules/players/v3/components/shared/achievement-card/achievement-card';
import {
  Card,
  CardContent,
  CardFilter,
  CardFilterType,
  CardHeader,
  CardTitle,
  type Filter,
} from '@/modules/players/v3/components/shared/card/card';
import { ACHIEVEMENT_COUNT_BADGE_CLASS_NAME } from '@/modules/players/v3/constants/achievements';
import { WebviewScreenId } from '@/modules/players/v3/constants/webview-screen-ids';
import { useAchievementCategoryFilters } from '@/modules/players/v3/hooks/use-achievement-category-filters';
import { useAuthorizeDashboard } from '@/modules/players/v3/hooks/use-authorize-dashboard';
import { useWebviewNavigation } from '@/modules/players/v3/hooks/use-webview-navigation';
import type { Achievement } from '@/modules/players/v3/types/achievements';
import { AchievementCategory } from '@/types/achievement.types';

const CATEGORY_FILTER_KEY = 'category';
const CATEGORY_SKELETON_KEYS = Array.from({ length: 3 }, () => crypto.randomUUID());
const CARD_SKELETON_KEYS = Array.from({ length: 4 }, () => crypto.randomUUID());
const SECTION_TITLE = formatText({ text: 'Achievements', role: TextRole.SectionHeader });
const SHOW_ALL_LABEL = formatText({ text: 'Show all', role: TextRole.ButtonLabel });
const FILTERS_LABEL = formatText({ text: 'Filters', role: TextRole.ButtonLabel });
const FILTER_HEADING = formatText({ text: 'Filter by Category', role: TextRole.ModalTitle });
const NO_MATCHES_TITLE = formatText({
  text: 'No achievements to show',
  role: TextRole.DescriptiveText,
});
const NO_MATCHES_DESCRIPTION = formatText({
  text: 'Try removing some filters to see more achievements.',
  role: TextRole.DescriptiveText,
});
const NO_ACHIEVEMENTS_TITLE = formatText({
  text: 'No achievements yet',
  role: TextRole.DescriptiveText,
});

interface PublicPlayerAchievementListProps {
  slug: string;
  categories: Pick<AchievementCategory, 'id' | 'name'>[];
  achievementsByCategory: Record<string, Achievement[]>;
  hasAnyAchievements: boolean;
  playerFirstName: string;
  isOwnProfile: boolean;
}

export const PublicPlayerAchievementList = ({
  slug,
  categories,
  achievementsByCategory,
  hasAnyAchievements,
  playerFirstName,
  isOwnProfile,
}: PublicPlayerAchievementListProps) => {
  const canSeeAllAchievements = useAuthorizeDashboard(isOwnProfile);
  const { handleNativeNavClick } = useWebviewNavigation();

  const { selectedIds, isPending, setSelectedCategories } = useAchievementCategoryFilters();

  const categoryOptions = categories.map((c) => ({ id: String(c.id), name: c.name }));

  const categoryFilters: Filter[] = [
    {
      key: CATEGORY_FILTER_KEY,
      type: CardFilterType.MULTIPLE,
      style: 'toggle',
      title: '',
      items: categoryOptions.map((option) => ({ label: option.name, value: option.id })),
    },
  ];

  const visibleCategories = categories
    .map((cat) => ({
      cat,
      achievements: achievementsByCategory[String(cat.id)] ?? [],
    }))
    .filter(({ cat, achievements }) => {
      if (achievements.length === 0) return false;
      if (selectedIds.length === 0) return true;
      return selectedIds.includes(String(cat.id));
    });

  return (
    <div className='flex flex-col gap-6 py-4'>
      <div className='flex items-center justify-between gap-4'>
        <h2 className='text-lg md:text-xl font-semibold text-gray-900'>{SECTION_TITLE}</h2>
        <div className='flex items-center gap-2'>
          {canSeeAllAchievements ? (
            <CustomLink
              href={`/players/${slug}/v3/profile/achievements`}
              onClick={
                isOwnProfile
                  ? (event) => handleNativeNavClick(event, WebviewScreenId.ACHIEVEMENTS)
                  : undefined
              }
            >
              <Button variant='secondary' className='shrink-0'>
                {SHOW_ALL_LABEL}
              </Button>
            </CustomLink>
          ) : null}
          {hasAnyAchievements ? (
            <CardFilter
              immediate
              filters={categoryFilters}
              label={FILTERS_LABEL}
              heading={{ title: FILTER_HEADING }}
              initialSelectedFilters={{ [CATEGORY_FILTER_KEY]: selectedIds }}
              hasActiveFilters={selectedIds.length > 0}
              onApply={(next) => setSelectedCategories(next)}
            />
          ) : null}
        </div>
      </div>

      {isPending ? (
        <>
          {CATEGORY_SKELETON_KEYS.map((categoryKey) => (
            <Card key={categoryKey} className='bg-white'>
              <CardHeader>
                <CardTitle className='text-gray-700'>
                  <div className='h-5 w-32 bg-gray-200 rounded animate-pulse' />
                </CardTitle>
              </CardHeader>
              <CardContent className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                {CARD_SKELETON_KEYS.map((cardKey) => (
                  <AchievementCardSkeleton key={`${categoryKey}-${cardKey}`} />
                ))}
              </CardContent>
            </Card>
          ))}
        </>
      ) : visibleCategories.length > 0 ? (
        visibleCategories.map(({ cat, achievements }) => (
          <Card key={String(cat.id)} className='bg-white'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <CardTitle className='text-gray-700'>{cat.name}</CardTitle>
                <span className={ACHIEVEMENT_COUNT_BADGE_CLASS_NAME}>
                  {achievements.length}
                </span>
              </div>
            </CardHeader>
            <CardContent className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
              {achievements.map((achievement) => (
                <AchievementCard
                  key={achievement.achievementId}
                  achievement={achievement}
                  showShare={false}
                />
              ))}
            </CardContent>
          </Card>
        ))
      ) : selectedIds.length > 0 ? (
        <MascotEmptyState
          title={NO_MATCHES_TITLE}
          description={NO_MATCHES_DESCRIPTION}
        />
      ) : (
        <MascotEmptyState
          title={NO_ACHIEVEMENTS_TITLE}
          description={`${playerFirstName} hasn’t earned any achievements yet. Check back soon.`}
        />
      )}
    </div>
  );
};
